import React, { useState, useEffect } from "react";
import styles from "./EligibilityRecommendedProgram.module.css";
import EligibilityPricingContainer from "../EligibilityPriceCard/EligibilityPricingContainer";
import { fetchProgramDetails } from "@/services/PreEnrollmentService";
import useIsMobile from "@/hooks/useIsMobile";

import {
  CalendarDays,
  Banknote,
  ArrowRight,
  ArrowLeft,
  ChevronLeft,
} from "lucide-react";
import { trackEventPreEnrolment } from "@/services/mixpanel";

const convertToSlug = (text: string) => {
  return text
    .toLowerCase()
    .replace(/ /g, "-")
    .replace(/[^\w-]+/g, "");
};

interface ProgramAmount {
  contact?: number;
  online?: number;
}

interface Program {
  currentNQF: string;
  id: string;
  name: string;
  nqfLevel: string;
  noOfYear: number;
  totalAmount?: ProgramAmount[];
  code?: string;
  slug?: string;
  school: string;
}

const EligibilityRecommendedProgram = ({
  nqtLevel,
  programsList,
  onBack,
  windowHeight,
  selectedProgramField,
}: {
  nqtLevel: string;
  programsList: Program[];
  onBack: () => void;
  windowHeight: any;
  selectedProgramField: any;
}) => {
  const [selectedProgram, setSelectedProgram] = useState<Program | null>(null);
  const [currentStep, setCurrentStep] = useState<number>(1);
  const [priceData, setPriceData] = useState<any>(null);
  const [eligibilityFormData, setEligibilityFormData] = useState<any>(null);

  const getTotalAmount = (program: Program) => {
    const totalAmount = program?.totalAmount;
    if (totalAmount) {
      const contactAmount = totalAmount.find(
        (amount) => amount.contact
      )?.contact;
      const onlineAmount = totalAmount.find((amount) => amount.online)?.online;

      return {
        amount: contactAmount || onlineAmount,
        studyMode: contactAmount ? "contact" : "online",
      };
    }
    return null;
  };

  if (selectedProgram) {
    localStorage.setItem(
      "userSelectedProgram",
      JSON.stringify(selectedProgram?.name)
    );
  }

  useEffect(() => {
    if (selectedProgram && selectedProgram?.code) {
      fetchProgramDetails(selectedProgram.code)
        .then((data) => setPriceData(data))
        .catch((error) => console.error("Error fetching data:", error));
    }
  }, [selectedProgram]);

  const targetSchool = selectedProgramField.school;

  const filteredPrograms = programsList
    .filter((program) => program.currentNQF === nqtLevel.toString())
    .sort((a, b) =>
      a.school === targetSchool ? -1 : b.school === targetSchool ? 1 : 0
    );

  const displayedPrograms = filteredPrograms;

  const handleCardClick = (program: Program) => {
    setSelectedProgram(program);
  };

  const handleReserveClick = () => {
    if (selectedProgram) {
      setSelectedProgram({
        ...selectedProgram,
        slug: convertToSlug(selectedProgram.name),
      });
      setCurrentStep(2);
      const formData = {
        rmatFee: priceData?.data[0]?.rmatFee,
        rplFee: priceData?.data[0]?.rplFee,
      };
      localStorage.setItem(
        "totalFee",
        JSON.stringify(formData.rmatFee + formData.rplFee)
      );
      trackEventPreEnrolment("program_recommendation_clicked", {
        program_name: selectedProgram?.name,
      });
    }
  };

  const handleBack = () => {
    setCurrentStep(1);
  };
  useEffect(() => {
    const savedState = localStorage.getItem("FinalSelectedProgram");
    if (savedState) {
      const state = JSON.parse(savedState);
      setSelectedProgram(state.selectedProgram);
      setEligibilityFormData(state.eligibilityFormData);
    }
  }, []);

  useEffect(() => {
    const state = {
      selectedProgram,
      eligibilityFormData,
    };
    localStorage.setItem("FinalSelectedProgram", JSON.stringify(state));
  }, [selectedProgram, eligibilityFormData]);

  useEffect(() => {
    localStorage.setItem("programCurrentStep", currentStep.toString());
  }, [currentStep]);

  useEffect(() => {
    if (selectedProgram && priceData) {
      const totalAmountInfo = getTotalAmount(selectedProgram);
      const selectedStudyMode = priceData?.data[0]?.studyModes.find(
        (mode: any) =>
          (totalAmountInfo?.studyMode === "online" &&
            mode.studyModeCode === "ONLINE") ||
          (totalAmountInfo?.studyMode === "contact" &&
            (mode.studyModeCode === "FULL-TIME" ||
              mode.studyModeCode === "DAY"))
      );

      const formData = {
        programCode: priceData?.data[0]?.programCode,
        programName: priceData?.data[0]?.programName,
        studyModes: selectedStudyMode,
        rmatFee: priceData?.data[0]?.rmatFee,
        rplFee: priceData?.data[0]?.rplFee,
        currentNqfLevel: selectedProgram.nqfLevel,
      };

      setEligibilityFormData(formData);
      localStorage.setItem("selectedProgram", JSON.stringify(selectedProgram));
      localStorage.setItem("eligibilityFormData", JSON.stringify(formData));
    }
  }, [selectedProgram, priceData]);

  const isMobile = useIsMobile();

  return (
    <>
      {currentStep === 1 && (
        <div className={styles.container}>
          <div onClick={onBack} className={styles.cancleDesktop}>
            <ArrowLeft />
          </div>
          <div className={styles.TopTag}>
            <div className={styles.headTopTag}>
              <div onClick={onBack}>
                <ChevronLeft />
              </div>
              <span>ⓘ 2 minutes to get provisional enrolment</span>
            </div>
            <h2 className={styles.heading}>You&#39;re Just One Step Away!</h2>
          </div>
          <div
            className={styles.section}
            style={{
              height: isMobile ? "auto" : `${windowHeight - 120}px`,
              overflowY: displayedPrograms.length > 4 ? "scroll" : "auto",
            }}
          >
            <div className={styles.sectionContainer}>
              <p className={styles.subheading}>
                We recommend programmes that are perfectly aligned with your
                goals and tailored to match your aspirations.
              </p>
              <div className={styles.programList}>
                {displayedPrograms.map((program) => {
                  const totalAmountInfo = getTotalAmount(program);
                  return (
                    <div
                      key={program.id}
                      className={`${styles.programCard} ${
                        selectedProgram?.id === program.id
                          ? styles.selected
                          : ""
                      }`}
                      onClick={() => handleCardClick(program)}
                    >
                      <div className={styles.contentDetail}>
                        <h3 className={styles.title}>{program.name}</h3>
                        <div className={styles.detailsContent}>
                          <p className={styles.details}>
                            <CalendarDays />
                            {program.noOfYear} Year
                            {program.noOfYear > 1 ? "s" : ""}
                          </p>
                          {totalAmountInfo && (
                            <p className={styles.details}>
                              <Banknote />R {totalAmountInfo.amount}
                            </p>
                          )}
                        </div>
                      </div>
                      <a
                        href={`/${convertToSlug(program.name)}`}
                        className={styles.learnMore}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => {
                          e.preventDefault();
                          trackEventPreEnrolment("learn_more_clicked", {
                            program_name: program?.name,
                          });
                          window.open(
                            `/${convertToSlug(program.name)}`,
                            "_blank"
                          );
                        }}
                      >
                        Learn More <ArrowRight />
                      </a>
                    </div>
                  );
                })}
              </div>
              <div className={styles.buttonsContainer}>
                <button
                  className={`${styles.submitButton} ${
                    !selectedProgram ? styles.disabled : ""
                  }`}
                  onClick={handleReserveClick}
                  disabled={!selectedProgram}
                >
                  Reserve Your Seat
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {currentStep === 2 && (
        <EligibilityPricingContainer
          selectedProgram={selectedProgram}
          onBack={handleBack}
          eligibilityFormData={eligibilityFormData}
          windowHeight={windowHeight}
        />
      )}
    </>
  );
};

export default EligibilityRecommendedProgram;
